import { AlertTriangle, BadgeCheck, Database, FlaskConical, GitCommitHorizontal, Info, MapPin, Route } from "lucide-react";
import type { QueryWarning, ResultEnvelope } from "@cd/core";
import { addDays, describeRange, iso } from "../../lib/time";
import { Badge, Tip } from "../ui/primitives";

export function TrustBadge({ certified, compact }: { certified: boolean; compact?: boolean }) {
  return certified ? (
    <Tip content="Every metric here is a GoTab-certified definition.">
      <Badge tone="good">
        <BadgeCheck className="size-3" aria-hidden />
        {!compact && "Certified"}
        {compact && <span className="sr-only">Certified</span>}
      </Badge>
    </Tip>
  ) : (
    <Tip content="Includes a custom formula built from certified metrics. The formula itself isn't certified.">
      <Badge tone="info">
        <FlaskConical className="size-3" aria-hidden />
        {!compact && "Custom"}
        {compact && <span className="sr-only">Custom</span>}
      </Badge>
    </Tip>
  );
}

export function WarningList({ warnings, dense }: { warnings: QueryWarning[]; dense?: boolean }) {
  if (!warnings.length) return null;
  return (
    <ul className="space-y-1.5">
      {warnings.map((w, i) => (
        <li
          key={i}
          className={`flex gap-2 rounded-lg px-2.5 py-1.5 text-xs leading-snug ${w.severity === "warn" ? "bg-warn-bg text-warn-fg" : "bg-info-bg text-info-fg"}`}
        >
          {w.severity === "warn" ? <AlertTriangle className="mt-px size-3.5 shrink-0" aria-hidden /> : <Info className="mt-px size-3.5 shrink-0" aria-hidden />}
          <span className={dense ? "line-clamp-2" : undefined}>{w.message}</span>
        </li>
      ))}
    </ul>
  );
}

const Section = ({ icon: Icon, title, children }: { icon: typeof Info; title: string; children: React.ReactNode }) => (
  <section className="border-b border-border px-5 py-4 last:border-0">
    <h3 className="mb-2.5 flex items-center gap-1.5 text-xs font-semibold text-muted">
      <Icon className="size-3.5" aria-hidden /> {title}
    </h3>
    {children}
  </section>
);

/** The day after a `YYYY-MM-DD` date: where the SQL's half-open date range stops. */
const nextDay = (day: string) => iso(addDays(new Date(`${day}T00:00:00`), 1));

/** "Show your work": how every number in a widget was computed. */
export function Provenance({ result, filterLabels }: {
  result: Pick<ResultEnvelope, "provenance" | "warnings" | "certified"> & Partial<ResultEnvelope>;
  /** Labels of the page's filters by field, to name the ones this widget left out. */
  filterLabels?: Record<string, string>;
}) {
  const p = result.provenance;
  return (
    <div className="text-sm">
      <div className="flex items-center gap-2 px-5 pt-4">
        <TrustBadge certified={result.certified} />
        {result.asOf && (
          <span className="text-xs text-subtle">
            Data as of {new Date(result.asOf).toLocaleTimeString([], { hour: "numeric", minute: "2-digit" })}
            {result.cached ? " · cached" : ""}
            {typeof result.durationMs === "number" ? ` · ${result.durationMs} ms` : ""}
          </span>
        )}
      </div>
      {result.warnings.length > 0 && (
        <div className="px-5 pt-3">
          <WarningList warnings={result.warnings} />
        </div>
      )}
      <Section icon={BadgeCheck} title="Metrics">
        <ul className="space-y-3">
          {p.metrics.map((m) => (
            <li key={m.key}>
              <div className="flex items-center gap-2">
                <span className="font-medium">{m.label}</span>
                <TrustBadge certified={m.certified} compact />
              </div>
              <p className="mt-0.5 text-muted">{m.description}</p>
              {m.note && <p className="mt-1 text-xs text-subtle">{m.note}</p>}
              <code className="mt-1 block rounded-md bg-surface-2 px-2 py-1 font-mono text-[11px] text-muted">{m.definition}</code>
              {m.changes && m.changes.length > 0 && (
                <p className="mt-1 flex items-center gap-1 text-[11px] text-subtle">
                  <GitCommitHorizontal className="size-3" aria-hidden /> v{m.changes[0]!.version} ({m.changes[0]!.date}): {m.changes[0]!.note}
                </p>
              )}
            </li>
          ))}
        </ul>
      </Section>
      <Section icon={MapPin} title="Scope & filters">
        <ul className="space-y-1 text-muted">
          <li>{p.scope.description}</li>
          {p.timeRanges
            ? p.timeRanges.map((t) => (
                <li key={t.range.start}>
                  Dates: {describeRange(t.range)} for {t.locationCount} location{t.locationCount === 1 ? "" : "s"} (in their own time zone)
                </li>
              ))
            : p.timeRange && <li>Dates: {describeRange(p.timeRange)}</li>}
          {result.comparisons
            ? result.comparisons.map((c, i) => c.timeRange && <li key={i}>Compared with: {describeRange(c.timeRange)}</li>)
            : result.compareTimeRange && <li>Compared with: {describeRange(result.compareTimeRange)}</li>}
          {p.filters.map((f) => <li key={f}>{f}</li>)}
          {p.skippedFilters?.map((f) => <li key={f.ref}>Not filtered by {filterLabels?.[f.ref] ?? "a page filter"}: {f.reason}</li>)}
        </ul>
      </Section>
      <Section icon={Route} title="How the data is connected">
        <ul className="space-y-1 text-muted">
          {p.joinPaths.map((j) => <li key={j}>{j}</li>)}
        </ul>
        <p className="mt-2 text-xs text-subtle">
          Each metric is totalled on its own before being combined, so nothing is counted twice.
        </p>
      </Section>
      {p.sql && (
        <Section icon={Database} title="Generated SQL (staff only)">
          {p.sql.map((s, i) => (
            <pre key={i} className="mb-2 max-h-72 overflow-auto rounded-lg bg-surface-2 p-3 font-mono text-[11px] leading-relaxed text-muted">{s}</pre>
          ))}
          {p.timeRange && p.timeRange.end.length <= 10 && (
            <p className="mb-1 text-[11px] text-subtle">
              Dates run through the end of {p.timeRange.end}: the SQL stops before {nextDay(p.timeRange.end)}, so orders late on the last day still count.
            </p>
          )}
          <p className="text-[11px] text-subtle">Semantic model {p.registryVersion}</p>
        </Section>
      )}
    </div>
  );
}
